/**
 * Instruments code with iteration counting to prevent infinite loops at runtime
 * @param {string} code - The code to instrument
 * @param {number} maxIterations - Maximum iterations before throwing error
 * @returns {string} - Instrumented code
 */
export const instrumentCodeWithLoopProtection = (code, maxIterations = 100000) => {
  const counterVar = '__loopIterCount__';
  const checkVar = '__checkLoopIter__';

  const checkFunction = `
    let ${counterVar} = 0;
    const ${checkVar} = () => {
      if (++${counterVar} > ${maxIterations}) {
        throw new Error('Infinite loop detected: Code exceeded ${maxIterations} iterations. Please check your while/for loops and recursive functions.');
      }
    };
  `;

  // Instrument while loops: while(...) => while(...) { __checkLoopIter__(); ...original body... }
  let instrumentedCode = code.replace(/while\s*\([^)]+\)\s*{/g, (match) => `${match}\n  ${checkVar}();`);

  // Also handle while loops without braces: while(...) statement;
  instrumentedCode = instrumentedCode.replace(/while\s*\([^)]+\)(?!\s*{)([^;]+;)/g, (match, body) => {
    return match.replace(body, `{ ${checkVar}(); ${body} }`);
  });

  // Instrument for loops: for(...) { => for(...) { __checkLoopIter__();
  instrumentedCode = instrumentedCode.replace(/for\s*\([^)]+\)\s*{/g, (match) => `${match}\n  ${checkVar}();`);

  // Handle for loops without braces
  instrumentedCode = instrumentedCode.replace(/for\s*\([^)]+\)(?!\s*{)([^;]+;)/g, (match, body) => {
    return match.replace(body, `{ ${checkVar}(); ${body} }`);
  });

  // Instrument do-while loops
  instrumentedCode = instrumentedCode.replace(/do\s*{/g, (match) => `${match}\n  ${checkVar}();`);

  // Prepend the check function and return instrumented code
  return checkFunction + '\n' + instrumentedCode;
};
